import { useState, useEffect } from "react";
import { FaClock, FaPlay, FaPause, FaRedoAlt, FaUser } from "react-icons/fa";
import { useGnosis } from "../context/GnosisContext";

// Compact Timer Card Component
const TimerCard = ({ id }) => {
  const {
    timers,
    formatTime,
    updateRecipient,
    updateDuration,
    toggleTimer,
    resetTimer,
    getTimerStatus
  } = useGnosis();

  const timer = timers[id];

  // Get color classes using updated color scheme
  const getColorClasses = () => ({
    card: "border-none",
    header: "bg-black dark:bg-uasYellow text-white dark:text-black border-none",
    button: "bg-black hover:bg-gray-800 text-white dark:bg-uasYellow dark:hover:bg-yellow-500 dark:text-black focus:ring-yellow-300 dark:focus:ring-yellow-500",
    icon: "bg-black dark:bg-uasYellow text-white dark:text-black",
    timer: timer.targetReached
      ? "text-red-500 dark:text-red-400"
      : timer.isActive
        ? "text-gray-700 dark:text-uasYellow"
        : "text-gray-500 dark:text-gray-300"
  });

  const colors = getColorClasses();

  // Get target time in MM:SS format
  const getTargetTime = () => {
    const targetSeconds = parseInt(timer.duration) * 60;
    return formatTime(targetSeconds);
  };

  // Get timer status and color
  const getStatusDisplay = () => {
    const status = getTimerStatus(timer);
    
    switch(status) {
      case "Overtime":
        return <span className="text-red-500 dark:text-red-400">Overtime</span>;
      case "Running":
        return <span className="text-green-500 dark:text-green-400">Running</span>;
      case "Paused":
        return <span className="text-uasYellow dark:text-uasYellow">Paused</span>;
      case "Ready":
      default:
        return <span className="text-gray-500 dark:text-gray-400">Ready</span>;
    }
  };

  return (
    <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md ${colors.card} transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1`}>
      {/* Compact Card Header */}
      <div className={`rounded-t-lg px-3 py-2 ${colors.header} border-b flex items-center justify-between`}>
        <h3 className="text-sm font-bold flex items-center">
          <FaClock className="mr-1" />
          {timer.recipient ? `Timer for ${timer.recipient}` : `Timer ${id}`}
        </h3>
        <div className="text-xs font-medium px-2 py-0.5 rounded bg-white bg-opacity-30 dark:bg-white">
          Timer {id}
        </div>
      </div>

      {/* Compact Card Body */}
      <div className="p-3">
        {/* Recipient Input */}
        <div className="mb-2">
          <label htmlFor={`recipient-${id}`} className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Recipient
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
              <FaUser className="w-3 h-3 text-gray-500 dark:text-gray-400" />
            </div>
            <input
              type="text"
              id={`recipient-${id}`}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-opacity-100 block w-full pl-8 p-2"
              placeholder="Enter recipient name"
              value={timer.recipient}
              onChange={(e) => updateRecipient(id, e.target.value)}
              required
            />
          </div>
        </div>

        {/* Duration Select */}
        <div className="mb-2">
          <label htmlFor={`duration-${id}`} className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Duration
          </label>
          <select
            id={`duration-${id}`}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-opacity-100 block w-full p-2"
            value={timer.duration}
            onChange={(e) => updateDuration(id, e.target.value)}
            disabled={timer.isActive}
          >
            <option value="1">1 Minute</option>
            <option value="2">2 Minutes</option>
            <option value="3">3 Minutes</option>
            <option value="4">4 Minutes</option>
          </select>
        </div>

        {/* Timer Display */}
        <div className="mb-2 mt-3 text-center">
          <div className={`text-3xl font-bold ${colors.timer} transition-all duration-300`}>
            {formatTime(timer.elapsedTime)}
          </div>
          
          {/* Target time display */}
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Target: {getTargetTime()}
          </div>
        </div>

        {/* Timer Controls */}
        <div className="flex justify-center space-x-2">
          <button
            type="button"
            onClick={() => toggleTimer(id)}
            className={`relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group ${timer.isActive ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-black to-gray-800 dark:from-uasYellow dark:to-yellow-500'}`}
          >
            <span className="relative px-3 py-1.5 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center">
              {timer.isActive ? (
                <>
                  <FaPause className="mr-1" /> Pause
                </>
              ) : (
                <>
                  <FaPlay className="mr-1" /> Start
                </>
              )}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              // Reset the timer
              resetTimer(id);
              // Then immediately start it (even if it was previously active)
              setTimeout(() => {
                // Force the timer to start by setting isActive to true directly
                // This ensures it starts regardless of previous state
                toggleTimer(id);
              }, 0);
            }}
            className="relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 dark:from-uasYellow dark:to-yellow-500"
          >
            <span className="relative px-3 py-1.5 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center">
              <FaRedoAlt className="mr-1" /> Restart
            </span>
          </button>
        </div>
        
        {/* Status Text */}
        <div className="mt-2 text-center text-xs font-medium">
          {getStatusDisplay()}
        </div>
      </div>
    </div>
  );
};

const Gnosis = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { timers } = useGnosis();

  useEffect(() => {
    // Reset animation state first
    setIsLoaded(false);

    // Small delay to ensure reset takes effect before starting animations
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="p-3 md:p-4">
      {/* Page Header */}
      <div className={`mb-4 transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
          Gnosis Timer System
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Set timers for different recipients and get notified when they expire.
        </p>
      </div>

      {/* Timer Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {/* Timer Cards */}
        {Object.values(timers).map((timer, index) => (
          <div
            key={timer.id}
            className={`transition-all duration-500 delay-${(index + 1) * 100} ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <TimerCard id={timer.id} />
          </div>
        ))}
      </div>

      {/* Instructions Panel */}
      <div className={`mt-4 bg-white dark:bg-laryzeDarkGray rounded-lg shadow p-3 transition-all duration-500 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">How to Use</h3>
        <ol className="space-y-1 text-xs text-gray-600 dark:text-gray-300 list-decimal list-inside">
          <li>Enter the recipient's name</li>
          <li>Select a target duration (1-4 minutes)</li>
          <li>Click Start to begin the timer</li>
          <li>The timer will count up from 00:00</li>
          <li>A notification will appear when the target time is reached</li>
          <li>Use Restart to reset and immediately restart the timer</li>
        </ol>
        <div className="mt-2 p-2 bg-uasYellow bg-opacity-20 dark:bg-uasYellow dark:bg-opacity-30 rounded-lg text-black dark:text-white text-xs">
          <strong>Note:</strong> Timers continue to run even when navigating to different pages.
        </div>
      </div>
    </div>
  );
};

export default Gnosis;